import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useGetGroupedInvitationsByProject } from "@/app/invitations/api/hooks";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import { InvitationsEmptyState } from "./components/my-invitations/invitations-empty-state";
import { getMyInvitationsColumns } from "./components/my-invitations/my-invitations-columns";

type MyInvitationsPageProps = {
  projectId?: string;
  focusedInvitationId?: string;
};

export function MyInvitationsPage({
  projectId,
  focusedInvitationId,
}: MyInvitationsPageProps) {
  const navigate = useNavigate({ from: ORG_PROJECT_ROUTE_PATH });
  const { data } = useGetGroupedInvitationsByProject(projectId);

  const invitations = data?.invitations ?? [];

  const sortedInvitations = useMemo(
    () =>
      [...invitations].sort((a, b) => {
        if (focusedInvitationId) {
          const aIsFocused =
            a.primary_invitation_id === focusedInvitationId ||
            a.inviters.some((i) => i.invitation_id === focusedInvitationId);
          const bIsFocused =
            b.primary_invitation_id === focusedInvitationId ||
            b.inviters.some((i) => i.invitation_id === focusedInvitationId);
          if (aIsFocused && !bIsFocused) {
            return -1;
          }
          if (!aIsFocused && bIsFocused) {
            return 1;
          }
        }
        return (
          new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime()
        );
      }),
    [invitations, focusedInvitationId]
  );

  const columns = useMemo(() => getMyInvitationsColumns(), []);

  const clearFilter = () => {
    navigate({
      to: "./invitations",
      search: {},
      replace: true,
    });
  };

  const hasInvitations = invitations.length > 0;
  const count = data?.invitationCount ?? 0;

  const filterBadge = projectId ? (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-1.5 text-sm">
      <span className="text-muted-foreground">Filtered</span>
      <Button
        className="h-auto p-0 font-medium"
        onClick={clearFilter}
        type="button"
        variant="link"
      >
        Clear
      </Button>
    </div>
  ) : null;

  return (
    <ListLayout
      actions={filterBadge}
      description="Pending project invitations for you to review"
      title={`My Invitations${count > 0 ? ` (${count})` : ""}`}
    >
      {hasInvitations ? (
        <DataTable
          className="space-y-4"
          columns={columns}
          data={sortedInvitations}
          defaultPageSize={10}
          getRowId={(row) => row.project_id}
        >
          <DataTableContent />
          <DataTablePagination />
        </DataTable>
      ) : (
        <InvitationsEmptyState
          hasFilter={Boolean(projectId)}
          onClearFilter={clearFilter}
        />
      )}
    </ListLayout>
  );
}
